import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// CAUCHERA Pages (Único Proyecto Activo)
import CaucheraLanding from "./pages/CaucheraLanding";
import CaucheraDashboard from "./pages/CaucheraDashboard";
import MicroTienda from "./pages/MicroTienda";
import OpportunityDetail from "./pages/OpportunityDetail";
import LoginPage from "./pages/LoginPage";
import PublicLanding from "./pages/PublicLanding";
import LegalPages from "./pages/LegalPages";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de ruta protegida
function ProtectedRoute({ children, isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Verificar token al cargar
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem("cauchera_token");
    
    if (!token) {
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    try {
      // Verificar que el token sea válido
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        // Token inválido, limpiar
        localStorage.removeItem("cauchera_token");
        localStorage.removeItem("cauchera_refresh_token");
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Error de red, mantener sesión si hay token
      setIsAuthenticated(true);
    }
    
    setCheckingAuth(false);
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("cauchera_token");
    localStorage.removeItem("cauchera_refresh_token");
    setIsAuthenticated(false);
  };

  // Mostrar loading mientras verifica auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* ==================== CAUCHERA - Sistema Clase Mundial ==================== */}
          
          {/* Login */}
          <Route 
            path="/login" 
            element={
              isAuthenticated 
                ? <Navigate to="/cauchera/dashboard" replace /> 
                : <LoginPage onLogin={handleLogin} />
            } 
          />
          
          {/* URL Corta para redirect externo */}
          <Route path="/go" element={<Navigate to="/login" replace />} />
          
          {/* Redirigir raíz a landing pública (para Stripe) */}
          <Route path="/" element={<PublicLanding />} />
          
          {/* Landing pública para Stripe Atlas */}
          <Route path="/home" element={<PublicLanding />} />
          
          {/* Landing page (pública) */}
          <Route path="/cauchera" element={<CaucheraLanding />} />
          <Route path="/cauchera/landing" element={<CaucheraLanding />} />
          
          {/* Dashboard CEO - PROTEGIDO */}
          <Route 
            path="/cauchera/dashboard" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CaucheraDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cauchera/demo" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CaucheraDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            } 
          />
          
          {/* Detalle de Oportunidad - PROTEGIDO */}
          <Route 
            path="/cauchera/opportunity/:opportunityId" 
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <OpportunityDetail />
              </ProtectedRoute>
            } 
          />
          
          {/* Micro-Tiendas (público - lo que ve el cliente final) */}
          <Route path="/tienda/:storeSlug" element={<MicroTienda />} />
          <Route path="/tienda/:storeSlug/success" element={<MicroTienda />} />
          
          {/* Páginas Legales (público) */}
          <Route path="/legal" element={<LegalPages />} />
          <Route path="/terminos" element={<LegalPages />} />
          <Route path="/privacidad" element={<LegalPages />} />
          <Route path="/reembolsos" element={<LegalPages />} />
          
          {/* Redirecciones de compatibilidad */}
          <Route path="/dashboard" element={<Navigate to="/cauchera/dashboard" replace />} />
          <Route path="/admin" element={<Navigate to="/cauchera/dashboard" replace />} />
          
          {/* 404 - Redirigir al login o dashboard */}
          <Route path="*" element={<Navigate to={isAuthenticated ? "/cauchera/dashboard" : "/login"} replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
